import { useEffect, useRef, useState } from "react";
import { Container } from "./styles";
import Auth from "../../Auth";

const newerVersion = (left, right) => left && (!right || left[0] > right[0] || (left[0] === right[0] && left[1] > right[1]));

export default function NewUserInterview() {
  const [activeLink, setActiveLink] = useState("");
  const [docId, setDocId] = useState("");
  const [error, setError] = useState("");
  const pendingWrites = useRef(0);
  const writeQueue = useRef(Promise.resolve());
  const latestSnapshot = useRef(null);
  const lastWriteVersion = useRef(null);
  const writeVersion = useRef(null);
  const writeGeneration = useRef(0);
  const applySnapshot = () => {
    const snapshot = latestSnapshot.current;
    const committed = lastWriteVersion.current;
    if (!snapshot || pendingWrites.current) return;
    if (committed && snapshot.version && (snapshot.version[0] < committed[0] || (snapshot.version[0] === committed[0] && snapshot.version[1] < committed[1]))) return;
    setActiveLink(snapshot.newInterviewLink);
  };
  useEffect(() => {
    let stopped = false;
    let controller;
    let timer;
    const reconnect = () => {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(connect, 1000);
    };
    const connect = () => {
      if (stopped) return;
      controller = new AbortController();
      Auth.streamWithIdToken("/api/admin/interview-link/stream", {}, controller.signal, (data) => {
        if (stopped || data.heartbeat) return;
        if (data.version && newerVersion(latestSnapshot.current?.version, data.version)) return;
        setDocId(data.id);
        latestSnapshot.current = data;
        applySnapshot();
        setError("");
      }, reconnect, () => {
        if (!stopped) setError("Unable to sync the interview link. Reconnecting…");
        reconnect();
      }).catch(() => reconnect());
    };
    connect();
    return () => { stopped = true; clearTimeout(timer); controller?.abort(); };
  }, []);

  const saveLink = (newInterviewLink) => {
    setActiveLink(newInterviewLink);
    if (!pendingWrites.current) {
      writeVersion.current = newerVersion(lastWriteVersion.current, latestSnapshot.current?.version)
        ? lastWriteVersion.current : latestSnapshot.current?.version;
    }
    const generation = writeGeneration.current;
    pendingWrites.current++;
    // Retain per-keystroke saving, but serialize requests so an older edit
    // cannot arrive after Clear or a newer link.
    writeQueue.current = writeQueue.current.catch(() => {}).then(async () => {
      try {
        if (generation !== writeGeneration.current) return;
        const discardPending = () => {
          writeGeneration.current++;
          setError("The link changed elsewhere. Pending edits were discarded.");
        };
        // An external update supersedes the whole queued edit batch. Do not
        // adopt its version and accidentally restore a claimed interview link.
        if (newerVersion(latestSnapshot.current?.version, writeVersion.current)) {
          discardPending();
          return;
        }
        const result = await Auth.fetchWithIdToken("/api/admin/interview-link/update", { id: docId, newInterviewLink, expectedVersion: writeVersion.current });
        if (result.conflict) {
          if (!latestSnapshot.current || !newerVersion(latestSnapshot.current.version, result.snapshot.version)) {
            latestSnapshot.current = result.snapshot;
            setDocId(result.snapshot.id);
          }
          lastWriteVersion.current = null;
          discardPending();
          return;
        }
        lastWriteVersion.current = result.version;
        writeVersion.current = result.version;
        setError("");
      } catch (err) {
        // A failed/uncertain save cannot safely supply the base for later edits.
        writeGeneration.current++;
        setError(err.message || "Unable to save the interview link.");
      } finally {
        pendingWrites.current--;
        applySnapshot();
      }
    });
  };
  const handleChange = (e) => saveLink(e.target.value);
  const handleClear = () => saveLink("");
  return (
    <Container>
      <h1>New User Interview</h1>
      <h2>Instructions</h2>
      <p className="mt-4">1. Open a Zoom meeting.</p>
      <p className="mt-4">2. Enable the waiting room.</p>
      <p className="mt-4">3. Participants - More - Play Join and Leave Sound</p>
      <p className="mt-4">4. Enable all participants to share their screen.</p>
      <p className="mt-4">5. Copy the link to the meeting and paste it into the text field below.</p>
      <p className="mt-4">6. When you paste the link, professional authors who are signing up will be offered $60 for 30 minutes of their time.</p>
      <p className="mt-4">7. When an author accepts the offer and clicks on the zoom link, the link will be cleared from this page so that no one else will get the offer.</p>
      <p className="mt-4">8. When the interview is over, if you want to do more, repeat the process.</p>
      {error && <p role="alert">{error}</p>}
      {activeLink && <button onClick={handleClear}>Clear Link</button>}
      <input
        disabled={!docId}
        type="text"
        value={activeLink}
        onChange={handleChange}
      />
      <h2 className="mt-8">The Customer's Experience</h2>
      <p className="mt-4">
        <a
          href="https://www.loom.com/share/908f9c190537403eb93fad54d7eaf7fb"
          target="_blank"
          rel="noopener noreferrer"
        >
          Watch a Demo
        </a>
      </p>
    </Container>
  );
}
